import React, { useState } from "react"
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Divider,
    Menu,
    MenuItem,
    Stack,
    Typography,
} from "@mui/material"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import { GuestItem } from "./GuestItem"
import { MeetingAttendee } from "../../meetings/meetingTypes"
import { makeStyles } from "tss-react/mui"
import { ManageAllButtonMenuOption } from "./GuestsSection"
import { ChevronRight } from "@mui/icons-material"

type GuestsListProps = {
    title: string
    guests: MeetingAttendee[] | undefined
    selectedIds: number[]
    onSelectGuest: (id: number) => void
    handleChange: (event: React.SyntheticEvent, expanded: boolean) => void
    expanded: boolean
    menuOptions: ManageAllButtonMenuOption[]
}

const useStyles = makeStyles()(() => ({
    guestsList: {
        display: "flex",
        flexDirection: "column",
    },
    divider: {
        margin: "0.5rem 0",
        borderColor: "#E2E8F0",
    },
    icon: {
        marginLeft: "0.5rem",
    },
    accordion: {
        ".MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
            transform: "rotate(90deg)",
        },
    },
}))

export function GuestsList({
    title,
    guests,
    selectedIds,
    onSelectGuest,
    menuOptions,
    handleChange,
    expanded,
}: GuestsListProps): JSX.Element {
    const { classes } = useStyles()
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const [menuOpen, setMenuOpen] = useState<boolean>(false)

    function handleMenuClick(event: React.MouseEvent<HTMLElement>): void {
        event.stopPropagation()
        setAnchorEl(event.currentTarget)
        setMenuOpen(true)
    }

    function handleMenuClose(event: React.MouseEvent<HTMLElement>): void {
        event.stopPropagation()
        setMenuOpen(false)
    }

    return (
        <Box>
            <Accordion expanded={expanded} onChange={handleChange}>
                <AccordionSummary
                    expandIcon={<ChevronRight className={classes.icon} />}
                    aria-controls="panel1bh-content"
                    id="panel1bh-header"
                    className={classes.accordion}
                >
                    <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
                        <Typography variant="subtitle1">
                            {title} ({guests?.length || 0}):
                        </Typography>
                        <LocalizedStrict id="service-center-reports-dashboard-meetings-drawer-manage-all-button">
                            <StandardButton onClick={handleMenuClick} fullWidth={false} variant="outlined" size="small">
                                Manage all
                            </StandardButton>
                        </LocalizedStrict>
                        <Menu
                            anchorEl={anchorEl}
                            open={menuOpen}
                            disableScrollLock
                            onClose={handleMenuClose}
                            anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "right",
                            }}
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                        >
                            {menuOptions.map((option) => (
                                <MenuItem key={option.label} onClick={option.onClick}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Menu>
                    </Stack>
                </AccordionSummary>
                <AccordionDetails>
                    <Box className={classes.guestsList}>
                        {guests?.map((guest) => (
                            <React.Fragment key={guest.id}>
                                <Divider className={classes.divider} />
                                <GuestItem attendee={guest} onSelect={onSelectGuest} selectedIds={selectedIds} />
                            </React.Fragment>
                        ))}
                    </Box>
                </AccordionDetails>
            </Accordion>
        </Box>
    )
}
